import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy } from './firestore-compat';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, LabelList,
  PieChart, Pie, Cell
} from 'recharts';

// =============================
// HELPERS DE DATA
// =============================
const isPast = (dateString) => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dataItem = new Date(dateString + 'T00:00:00');
  return dataItem < hoje;
};

const isFuture = (dateString) => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dataItem = new Date(dateString + 'T00:00:00');
  return dataItem >= hoje;
};

const getFirstDayOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const getToday = () => new Date().toISOString().split('T')[0];

const formatDateBR = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString + 'T00:00:00');
  return d.toLocaleDateString('pt-BR');
};

// =============================
// ESTILOS INLINE
// =============================
const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f4ff 0%, #fafbff 100%)',
    padding: '24px',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  headerTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: 0,
  },
  headerBadge: {
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: '700',
    padding: '3px 10px',
    borderRadius: '20px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  toolbarCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px 24px',
    marginBottom: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #e8ecf4',
  },
  toolbarSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'flex-end',
  },
  toolbarGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  toolbarLabel: {
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#8a94a6',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  toolbarInput: {
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    padding: '8px 12px',
    fontSize: '0.88rem',
    color: '#2d3748',
    background: '#fafafa',
    outline: 'none',
    minWidth: '140px',
    cursor: 'pointer',
  },
  divider: {
    width: '1px',
    height: '48px',
    background: '#e2e8f0',
    alignSelf: 'flex-end',
    marginBottom: '3px',
  },
  filterChipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #f0f4ff',
  },
  filterChipsLabel: {
    fontSize: '0.72rem',
    fontWeight: '700',
    color: '#8a94a6',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    minWidth: '80px',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 14px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: '1.5px solid #e2e8f0',
    background: '#f7f9fc',
    color: '#6b7280',
    transition: 'all 0.15s',
    userSelect: 'none',
  },
  chipActive: {
    background: '#667eea',
    borderColor: '#667eea',
    color: '#fff',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  kpiCard: (color) => ({
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: `1px solid #e8ecf4`,
    borderLeft: `4px solid ${color}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  }),
  kpiIcon: {
    fontSize: '1.5rem',
    marginBottom: '4px',
  },
  kpiValue: (color) => ({
    fontSize: '2.2rem',
    fontWeight: '800',
    color: color,
    lineHeight: 1,
  }),
  kpiLabel: {
    fontSize: '0.8rem',
    color: '#8a94a6',
    fontWeight: '500',
    marginTop: '4px',
  },
  kpiSub: {
    fontSize: '0.72rem',
    color: '#b0b8c9',
    fontWeight: '400',
  },
  sectionCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #e8ecf4',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.88rem',
  },
  th: {
    background: '#f7f9fc',
    padding: '12px 14px',
    textAlign: 'left',
    color: '#6b7280',
    fontWeight: '600',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e8ecf4',
  },
  td: {
    padding: '12px 14px',
    color: '#374151',
    borderBottom: '1px solid #f0f4ff',
    verticalAlign: 'top',
  },
  emptyMsg: {
    textAlign: 'center',
    color: '#b0b8c9',
    fontStyle: 'italic',
    padding: '40px 0',
    fontSize: '0.9rem',
  },
  badge: (color, bg) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: color,
    background: bg,
  }),
};

// =============================
// COMPONENTE PRINCIPAL
// =============================
function IndicadorAgenda() {
  const [auditorias, setAuditorias] = useState([]);
  const [tarefasManuais, setTarefasManuais] = useState([]);
  const [auditoresConfig, setAuditoresConfig] = useState([]);
  const [unidadesConfig, setUnidadesConfig] = useState([]);

  // Filtros de período
  const [startDate, setStartDate] = useState(getFirstDayOfMonth);
  const [endDate, setEndDate] = useState(getToday);

  // Filtros de tipo
  const [dataType, setDataType] = useState('agenda');
  const [filterCategory, setFilterCategory] = useState('todas');

  // Filtros de Auditores e Unidades (chips)
  const [activeAuditores, setActiveAuditores] = useState([]);
  const [activeUnidades, setActiveUnidades] = useState([]);

  // =============================
  // CARREGAR CONFIGS
  // =============================
  useEffect(() => {
    const unsubAuditors = onSnapshot(
      query(collection(db, 'auditors_config'), orderBy('name')),
      (snap) => {
        const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAuditoresConfig(loaded);
        setActiveAuditores(prev => prev.length === 0 ? loaded.map(a => a.name) : prev);
      }
    );
    const unsubUnits = onSnapshot(
      query(collection(db, 'units_config'), orderBy('name')),
      (snap) => {
        const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUnidadesConfig(loaded);
        setActiveUnidades(prev => prev.length === 0 ? loaded.map(u => u.name) : prev);
      }
    );
    return () => { unsubAuditors(); unsubUnits(); };
  }, []);

  // =============================
  // CARREGAR DADOS
  // =============================
  useEffect(() => {
    const unsubAud = onSnapshot(query(collection(db, 'auditorias')), (snap) => {
      setAuditorias(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubMan = onSnapshot(query(collection(db, 'qualidade_tarefas')), (snap) => {
      setTarefasManuais(snap.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'manual' })));
    });
    return () => { unsubAud(); unsubMan(); };
  }, []);

  // =============================
  // PROCESSAMENTO DOS DADOS
  // =============================
  const processedData = useMemo(() => {
    // Agenda = só auditorias | Tarefas = só tarefas manuais (separados, não somados)
    const sourceData = dataType === 'agenda'
      ? auditorias.filter(a => !a.isManualPlaceholder)
      : tarefasManuais;

    // Filtrar por intervalo de datas
    const start = startDate ? new Date(startDate + 'T00:00:00') : null;
    const end = endDate ? new Date(endDate + 'T23:59:59') : null;

    const itemsNoPeriodo = sourceData.filter(item => {
      if (!item.data) return false;
      const itemDate = new Date(item.data + 'T00:00:00');
      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;
      return true;
    });

    // Aplicar filtros de auditor e unidade
    const filteredItems = itemsNoPeriodo.filter(item => {
      // Filtro Auditor
      const itemAuditores = item.auditores || (item.auditor ? [item.auditor] : []);
      if (itemAuditores.length > 0) {
        const temAuditorAtivo = itemAuditores.some(audit =>
          activeAuditores.some(ativo => audit.includes(ativo) || ativo.includes(audit))
        );
        if (!temAuditorAtivo) return false;
      }

      // Filtro Unidade (só para itens não-manuais)
      if (item.type !== 'manual') {
        let unidadeNormalizada = item.unidade;
        if (['Home/Senior', 'Home / Senior', 'Início/Idosos', 'Inicio/Idosos'].includes(item.unidade)) {
          unidadeNormalizada = 'Homecare';
        } else if (!['Prontobaby', 'Homecare', 'CPL', 'CPB'].includes(item.unidade)) {
          unidadeNormalizada = 'Prontobaby';
        }
        if (item.unidade && !activeUnidades.includes(unidadeNormalizada)) return false;
      }

      // Filtro Categoria
      if (filterCategory !== 'todas' && item.categoria !== filterCategory) return false;

      return true;
    });

    // KPIs Principais
    const total = filteredItems.length;
    const totalFeitas = filteredItems.filter(a => a.status === 'Feito').length;
    const totalNaoRealizadas = filteredItems.filter(a =>
      a.status === 'Não Realizado' ||
      (isPast(a.data) && a.status !== 'Feito' && a.status !== 'Reagendado')
    ).length;

    // NOVO KPI: Não realizadas COM justificativa
    const totalNaoRealizadasComJustificativa = filteredItems.filter(a =>
      (a.status === 'Não Realizado' ||
        (isPast(a.data) && a.status !== 'Feito' && a.status !== 'Reagendado'))
      && !!a.justificativaNaoRealizado
    ).length;

    // % de realização (sobre os já passados, excluindo futuros)
    const itensPassados = filteredItems.filter(a =>
      a.status === 'Feito' ||
      a.status === 'Não Realizado' ||
      (isPast(a.data) && a.status !== 'Reagendado')
    ).length;
    const percRealizacao = itensPassados > 0
      ? Math.round((totalFeitas / itensPassados) * 100)
      : null;

    // Tabela de não realizadas COM justificativa (respeitando filtros ativos)
    const naoRealizadasData = filteredItems
      .filter(item =>
        (item.status === 'Não Realizado' ||
          (isPast(item.data) && item.status !== 'Feito' && item.status !== 'Reagendado'))
        && item.justificativaNaoRealizado
      )
      .map(item => ({
        id: item.id,
        unidade: item.unidade || 'N/A',
        auditor: item.auditor || (item.auditores ? item.auditores.join(', ') : 'N/A'),
        data: item.data,
        descricao: item.descricao || item.auditoria || item.titulo || '—',
        justificativa: item.justificativaNaoRealizado,
        status: item.status,
      }))
      .sort((a, b) => new Date(a.data) - new Date(b.data));

    // Gráfico por Auditor
    const dataPorAuditor = auditoresConfig
      .filter(conf => activeAuditores.includes(conf.name))
      .map(conf => {
        const auditor = conf.name;
        const itemsDoAuditor = filteredItems.filter(a => {
          const itemAuditores = a.auditores || (a.auditor ? [a.auditor] : []);
          return itemAuditores.some(audit => audit.includes(auditor) || auditor.includes(audit));
        });
        const realizadas = itemsDoAuditor.filter(a => a.status === 'Feito').length;
        const naoRealizadas = itemsDoAuditor.filter(a =>
          a.status === 'Não Realizado' ||
          (isPast(a.data) && a.status !== 'Feito' && a.status !== 'Reagendado')
        ).length;
        const aRealizar = itemsDoAuditor.filter(a =>
          a.status !== 'Feito' && a.status !== 'Reagendado' && a.status !== 'Não Realizado' && isFuture(a.data)
        ).length;

        return {
          name: auditor,
          color: conf.color || '#667eea',
          'Realizadas': realizadas,
          'Não Realizadas': naoRealizadas,
          'A Realizar': aRealizar,
        };
      })
      .filter(d => d['Realizadas'] > 0 || d['Não Realizadas'] > 0 || d['A Realizar'] > 0);

    // Dados para o Pie Chart
    const pieData = [
      { name: 'Realizadas', value: totalFeitas, color: '#10b981' },
      { name: 'Não Realizadas', value: totalNaoRealizadas, color: '#ef4444' },
      { name: 'A Realizar', value: total - totalFeitas - totalNaoRealizadas > 0 ? total - totalFeitas - totalNaoRealizadas : 0, color: '#3b82f6' },
    ].filter(d => d.value > 0);

    return {
      total,
      totalFeitas,
      totalNaoRealizadas,
      totalNaoRealizadasComJustificativa,
      percRealizacao,
      dataPorAuditor,
      naoRealizadasData,
      pieData,
    };
  }, [auditorias, tarefasManuais, dataType, startDate, endDate, activeAuditores, activeUnidades, filterCategory, auditoresConfig]);

  // =============================
  // TOGGLE CHIPS
  // =============================
  const toggleChip = (item, activeList, setActive, allNames) => {
    const isActive = activeList.includes(item);
    if (isActive) {
      setActive(activeList.length === 1 ? allNames : activeList.filter(i => i !== item));
    } else {
      setActive(activeList.length === allNames.length ? [item] : [...activeList, item]);
    }
  };

  const allAuditorNames = auditoresConfig.map(a => a.name);
  const allUnitNames = unidadesConfig.map(u => u.name);
  const CATEGORIAS = ['Auditoria', 'Treinamento', 'Tarefa Interna'];

  // =============================
  // KPI CONFIG
  // =============================
  const kpis = [
    {
      icon: '📋',
      value: processedData.total,
      label: dataType === 'agenda' ? 'Total de Agendas' : 'Total de Tarefas',
      sub: 'no período',
      color: '#3b82f6',
    },
    {
      icon: '✅',
      value: processedData.totalFeitas,
      label: 'Realizadas',
      sub: `de ${processedData.total}`,
      color: '#10b981',
    },
    {
      icon: '⚠️',
      value: processedData.totalNaoRealizadas,
      label: 'Não Realizadas',
      sub: 'inclui pendentes do passado',
      color: '#ef4444',
    },
    {
      icon: '📝',
      value: processedData.totalNaoRealizadasComJustificativa,
      label: 'Não Realizadas c/ Justificativa',
      sub: `de ${processedData.totalNaoRealizadas} não realizadas`,
      color: '#f59e0b',
    },
    {
      icon: '📊',
      value: processedData.percRealizacao !== null ? `${processedData.percRealizacao}%` : '—',
      label: '% de Realização',
      sub: 'atividades já vencidas',
      color: '#8b5cf6',
    },
  ];

  // =============================
  // RENDER
  // =============================
  return (
    <div style={styles.page}>

      {/* HEADER */}
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>Indicadores da Qualidade</h2>
        <span style={styles.headerBadge}>Agenda</span>
      </div>

      {/* TOOLBAR (Filtros) */}
      <div style={styles.toolbarCard}>
        <div style={styles.toolbarSection}>

          {/* Tipo de dado */}
          <div style={styles.toolbarGroup}>
            <span style={styles.toolbarLabel}>Visualizar</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { key: 'agenda', label: '📅 Agenda' },
                { key: 'tarefas', label: '✅ + Tarefas' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setDataType(opt.key)}
                  style={{
                    ...styles.toolbarInput,
                    background: dataType === opt.key ? '#667eea' : '#fafafa',
                    color: dataType === opt.key ? '#fff' : '#2d3748',
                    borderColor: dataType === opt.key ? '#667eea' : '#e2e8f0',
                    fontWeight: dataType === opt.key ? '600' : '400',
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.divider} />

          {/* Data Início */}
          <div style={styles.toolbarGroup}>
            <span style={styles.toolbarLabel}>De:</span>
            <input
              type="date"
              style={styles.toolbarInput}
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {/* Data Fim */}
          <div style={styles.toolbarGroup}>
            <span style={styles.toolbarLabel}>Até:</span>
            <input
              type="date"
              style={styles.toolbarInput}
              value={endDate}
              min={startDate}
              max={getToday()}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div style={styles.divider} />

          {/* Categoria */}
          <div style={styles.toolbarGroup}>
            <span style={styles.toolbarLabel}>Categoria</span>
            <select
              style={styles.toolbarInput}
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="todas">Todas</option>
              {CATEGORIAS.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Atalhos de período */}
          <div style={styles.toolbarGroup}>
            <span style={styles.toolbarLabel}>Atalhos</span>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                {
                  label: 'Este mês', onClick: () => {
                    setStartDate(getFirstDayOfMonth());
                    setEndDate(getToday());
                  }
                },
                {
                  label: 'Mês passado', onClick: () => {
                    const d = new Date();
                    const firstOfLastMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1);
                    const lastOfLastMonth = new Date(d.getFullYear(), d.getMonth(), 0);
                    setStartDate(firstOfLastMonth.toISOString().split('T')[0]);
                    setEndDate(lastOfLastMonth.toISOString().split('T')[0]);
                  }
                },
                {
                  label: 'Este ano', onClick: () => {
                    const year = new Date().getFullYear();
                    setStartDate(`${year}-01-01`);
                    setEndDate(getToday());
                  }
                },
              ].map(btn => (
                <button
                  key={btn.label}
                  onClick={btn.onClick}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1.5px solid #e2e8f0',
                    background: '#f7f9fc',
                    color: '#6b7280',
                    fontSize: '0.78rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CHIPS: Auditores */}
        <div style={styles.filterChipsRow}>
          <span style={styles.filterChipsLabel}>Auditores:</span>
          <div
            style={{
              ...styles.chip,
              ...(activeAuditores.length === allAuditorNames.length ? styles.chipActive : {}),
            }}
            onClick={() => setActiveAuditores(allAuditorNames)}
          >
            Todos
          </div>
          {auditoresConfig.map(a => {
            const isActive = activeAuditores.includes(a.name) && activeAuditores.length !== allAuditorNames.length;
            return (
              <div
                key={a.id}
                style={{
                  ...styles.chip,
                  ...(isActive ? { backgroundColor: a.color, borderColor: a.color, color: '#fff' } : {}),
                }}
                onClick={() => toggleChip(a.name, activeAuditores, setActiveAuditores, allAuditorNames)}
              >
                {a.name}
              </div>
            );
          })}
        </div>

        {/* CHIPS: Unidades */}
        <div style={{ ...styles.filterChipsRow, borderTop: 'none', paddingTop: '8px' }}>
          <span style={styles.filterChipsLabel}>Unidades:</span>
          <div
            style={{
              ...styles.chip,
              ...(activeUnidades.length === allUnitNames.length ? styles.chipActive : {}),
            }}
            onClick={() => setActiveUnidades(allUnitNames)}
          >
            Todas
          </div>
          {unidadesConfig.map(u => {
            const isActive = activeUnidades.includes(u.name) && activeUnidades.length !== allUnitNames.length;
            return (
              <div
                key={u.id}
                style={{
                  ...styles.chip,
                  ...(isActive ? { backgroundColor: u.color, borderColor: u.color, color: '#fff' } : {}),
                }}
                onClick={() => toggleChip(u.name, activeUnidades, setActiveUnidades, allUnitNames)}
              >
                {u.name}
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI GRID */}
      <div style={styles.kpiGrid}>
        {kpis.map((kpi, i) => (
          <div key={i} style={styles.kpiCard(kpi.color)}>
            <div style={styles.kpiIcon}>{kpi.icon}</div>
            <div style={styles.kpiValue(kpi.color)}>{kpi.value}</div>
            <div style={styles.kpiLabel}>{kpi.label}</div>
            <div style={styles.kpiSub}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* GRÁFICOS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', marginBottom: '24px', alignItems: 'start' }}>

        {/* Gráfico de Barras por Auditor */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionTitle}>
            📊 Desempenho por Auditor
            <span style={{ fontSize: '0.75rem', color: '#b0b8c9', fontWeight: '400', marginLeft: '4px' }}>
              {startDate && endDate ? `${formatDateBR(startDate)} – ${formatDateBR(endDate)}` : ''}
            </span>
          </div>
          {processedData.dataPorAuditor.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={processedData.dataPorAuditor} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f4ff" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6b7280' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                <Tooltip
                  contentStyle={{ borderRadius: '10px', border: '1px solid #e8ecf4', fontSize: '0.85rem' }}
                />
                <Legend wrapperStyle={{ fontSize: '0.82rem' }} />
                <Bar dataKey="Realizadas" fill="#10b981" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="Realizadas" position="top" formatter={v => v > 0 ? v : ''} style={{ fontSize: '11px', fill: '#374151', fontWeight: '700' }} />
                </Bar>
                <Bar dataKey="Não Realizadas" fill="#ef4444" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="Não Realizadas" position="top" formatter={v => v > 0 ? v : ''} style={{ fontSize: '11px', fill: '#374151', fontWeight: '700' }} />
                </Bar>
                <Bar dataKey="A Realizar" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="A Realizar" position="top" formatter={v => v > 0 ? v : ''} style={{ fontSize: '11px', fill: '#374151', fontWeight: '700' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.emptyMsg}>Nenhum dado para exibir no período selecionado.</div>
          )}
        </div>

        {/* Pizza Chart */}
        {processedData.pieData.length > 0 && (
          <div style={{ ...styles.sectionCard, minWidth: '260px' }}>
            <div style={styles.sectionTitle}>🍕 Distribuição Geral</div>
            <PieChart width={220} height={220}>
              <Pie
                data={processedData.pieData}
                cx={110}
                cy={100}
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
              >
                {processedData.pieData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [`${value}`, name]} />
            </PieChart>
            <div style={{ marginTop: '8px' }}>
              {processedData.pieData.map((entry, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '0.82rem' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
                  <span style={{ color: '#6b7280' }}>{entry.name}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: '700', color: '#374151' }}>{entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* TABELA DE JUSTIFICATIVAS */}
      <div style={styles.sectionCard}>
        <div style={styles.sectionTitle}>
          📝 Não Realizadas com Justificativa
          <span style={{
            marginLeft: '8px',
            background: '#fef3c7',
            color: '#92400e',
            fontSize: '0.75rem',
            padding: '2px 10px',
            borderRadius: '20px',
            fontWeight: '700',
          }}>
            {processedData.naoRealizadasData.length} registro{processedData.naoRealizadasData.length !== 1 ? 's' : ''}
          </span>
          {activeAuditores.length !== allAuditorNames.length && (
            <span style={{
              marginLeft: '6px',
              background: '#ede9fe',
              color: '#5b21b6',
              fontSize: '0.72rem',
              padding: '2px 8px',
              borderRadius: '20px',
              fontWeight: '600',
            }}>
              Filtrado por auditor
            </span>
          )}
        </div>

        {processedData.naoRealizadasData.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Data</th>
                  <th style={styles.th}>Auditor</th>
                  <th style={styles.th}>Unidade</th>
                  <th style={styles.th}>Descrição</th>
                  <th style={styles.th}>Justificativa</th>
                </tr>
              </thead>
              <tbody>
                {processedData.naoRealizadasData.map(item => (
                  <tr key={item.id} style={{ transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f7f9fc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ ...styles.td, whiteSpace: 'nowrap', fontWeight: '600', color: '#374151' }}>
                      {formatDateBR(item.data)}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge('#fff', auditoresConfig.find(a => item.auditor.includes(a.name))?.color || '#8b5cf6')}>
                        {item.auditor}
                      </span>
                    </td>
                    <td style={styles.td}>{item.unidade}</td>
                    <td style={{ ...styles.td, maxWidth: '200px' }}>{item.descricao}</td>
                    <td style={{ ...styles.td, color: '#6b7280', fontStyle: 'italic', maxWidth: '260px' }}>
                      "{item.justificativa}"
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyMsg}>
            Nenhuma atividade não realizada com justificativa no período selecionado.
          </div>
        )}
      </div>

    </div>
  );
}

export default IndicadorAgenda;